import { useContext, FC } from 'react';
import { PurchaseHistory } from '@one-thd/sui-icons';
import {
  Alert,
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography,
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getOrderStatus } from '../../utils/order-status';
import { isCurbsidePickupEligible } from '../../utils/fulfillment';
import { useOrderDetails, orderDetailsDataModel } from '../../hooks/useOrderDetails';
import { selectFulfillmentGroup } from '../../utils/order-details';
import { FulfillmentAddressDetails } from './FulfillmentAddressDetails';
import { FulfillmentLineItems } from './FulfillmentLineItems';
import { FulfillmentDetailsActions } from './FulfillmentDetailsActions';
import { FulfillmentDeliveryTracker } from './FulfillmentDeliveryTracker';
import { FulfillmentDetailsSkeleton } from './FulfillmentDetailsSkeleton';
import { ALERT_MESSAGES } from '../../constants';
import { CurbsidePickup } from '../fulfillment-details-actions/CurbsidePickup';
import { IDistributionView } from '../../models/active-orders';

interface FulfillmentDetailsDrawerProps {
  open?: boolean;
  onClose?: () => void;
  /**
   * The card's summary. Identifies the order and titles the drawer; everything
   * inside the body comes from `orderDetails`.
   */
  distribution?: IDistributionView;
  customerType?: string;
  isMobile?: boolean;
}

export const FulfillmentDetailsDrawer: FC<FulfillmentDetailsDrawerProps> = ({
  open,
  onClose,
  distribution,
  customerType,
  isMobile,
}) => {
  const { isConsumerApp } = useContext(ExperienceContext);

  /**
   * Fetching is keyed off `open`, so a closed drawer never issues a request and
   * the carousel never fires one per card.
   */
  const {
    orderDetails,
    isReady,
    loading,
    refetch,
    skipped,
  } = useOrderDetails({
    orderNumber: distribution?.orderNumber,
    skip: !open,
  });

  if (!distribution) {
    return null;
  }

  const { statusText, date, timeWindow, deliveryStatusText } = getOrderStatus(distribution);
  const statusDate = timeWindow?.short ? date?.short : date?.long;
  const title = (
    <Typography variant="h4" weight="bold">
      {statusText} {statusDate}
      {timeWindow?.short && `, ${timeWindow.short}`}
    </Typography>
  );

  const curbsidePickupEligible = isCurbsidePickupEligible(orderDetails, distribution)
    && isConsumerApp;
  const storePickup = selectFulfillmentGroup(orderDetails, distribution)?.storePickup;

  /**
   * Without the requested order there is nothing truthful to render in the
   * body: the card behind the drawer still shows the `activeOrders` summary,
   * so the recovery is to close the drawer or retry.
   *
   * Retry only makes sense after an actual attempt. A skipped query (no order
   * number, or no signed-in customer) never called `fetchOrderDetails` in the
   * first place, so `refetch` early-returns on the same guard — a Retry button
   * there would be a control that provably does nothing.
   */
  const detailsUnavailable = (showRetry: boolean) => (
    <div className="sui-flex sui-flex-col sui-w-full sui-gap-4 sui-p-4">
      <Alert status="error">
        {ALERT_MESSAGES.ORDER_DETAILS_UNAVAILABLE}
      </Alert>
      {showRetry && (
        <Button variant="secondary" onClick={() => refetch()} data-testid="order-details-retry">
          Retry
        </Button>
      )}
    </div>
  );

  const renderBody = () => {
    // A closing drawer keeps its children mounted for the whole exit
    // transition (`TransitionFade` holds them until `exited`), and closing is
    // also what sets `skip` — so an unavailable state here would flash an
    // error over a perfectly loaded order on its way out.
    if (!open) return null;

    // Loading before anything else: with `notifyOnNetworkStatusChange` a retry
    // re-enters `loading`, and that is the only sign the button did anything.
    if (loading) return <FulfillmentDetailsSkeleton />;

    // Settled with nothing to render: a failed fetch, or a response that
    // resolved carrying no order at all. Neither is a state to keep
    // skeletoning — that shows a drawer that never stops loading.
    if (!isReady) return detailsUnavailable(!skipped);

    return (
      <>
        <div className="sui-bg-primary">
          <div className="sui-border-solid sui-border-b-1 sui-border-b-primary sui-pb-4">
            {(distribution?.projectName || distribution?.poJobName) && (
              <div className="sui-flex sui-flex-row sui-gap-2 sui-mb-1">
                {distribution?.projectName && (
                  <Badge color="info" size="small" variant="strong">
                    {distribution.projectName}
                  </Badge>
                )}
                {distribution?.poJobName && (
                  <Badge color="info" size="small" variant="strong">
                    {distribution.poJobName}
                  </Badge>
                )}
              </div>
            )}
            {orderDetails?.orderNumber && (
              <Typography variant="body-sm" color="subtle">
                Part of Order #{orderDetails.orderNumber}
              </Typography>
            )}
            <div className="sui-flex sui-flex-row sui-items-center sui-gap-2 sui-mt-4">
              <span className="sui-text-brand">
                <PurchaseHistory />
              </span>
              {/*
                Was "Delivery Status" for every order, which reads wrong on a
                pickup. The mobile design says "Order Status"; desktop still
                says "Delivery Status". The accurate one wins — see the design
                reference, Discrepancy 4.
              */}
              <Typography variant="h6">
                <span className="sui-text-brand">Order Status</span>
              </Typography>
            </div>
            <Typography
              variant="h4"
              weight="display"
              data-testid="delivery-status"
              uppercase
            >
              {deliveryStatusText}
            </Typography>
            <div className="sui-mt-3">
              <FulfillmentDeliveryTracker
                orderDetails={orderDetails}
                distribution={distribution}
                isMobile={isMobile}
              />
            </div>
          </div>

          <FulfillmentAddressDetails orderDetails={orderDetails} distribution={distribution} />
        </div>
        <div className="sui-bg-primary sui-mt-4 sui-mb-4 sui-p-4">
          <FulfillmentLineItems orderDetails={orderDetails} distribution={distribution} />
        </div>
        {curbsidePickupEligible && (
          <div className="sui-bg-primary sui-mt-4 sui-mb-4 sui-p-4">
            <CurbsidePickup
              storeId={storePickup?.store?.number ?? ''}
              orderId={orderDetails?.orderNumber ?? ''}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      data-testid="fulfillment-details-drawer"
    >
      <DrawerHeader title={title} onClose={onClose} />
      <DrawerBody overflow="scroll">
        {renderBody()}
      </DrawerBody>
      {/*
        The footer hangs off the same `isReady` the body does. `useOrderDetails`
        only reports it for a settled response belonging to the order currently
        requested, so the actions can never act on the previous order while the
        body shows the next one's skeleton or unavailable state.
      */}
      {isReady && (
        <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
          <FulfillmentDetailsActions
            orderDetails={orderDetails}
            customerType={customerType}
            distribution={distribution}
          />
        </DrawerFooter>
      )}
    </Drawer>
  );
};

// Lets `mockRequestResponse` build fixtures for the query this drawer fetches.
(FulfillmentDetailsDrawer as any).dataModel = orderDetailsDataModel;
